import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BookingConfirmationModal = ({ isOpen, onClose, shift, onConfirm, isLoading }) => {
  if (!isOpen || !shift) return null;

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`)?.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDepartmentLabel = (department) => {
    const labels = {
      'customer-service': 'Kundendienst',
      'operations': 'Betrieb',
      'manufacturing': 'Fertigung',
      'administration': 'Verwaltung',
      'maintenance': 'Wartung',
      'security': 'Sicherheit'
    };
    return labels?.[department] || department;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            Schicht buchen
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            disabled={isLoading}
          />
        </div>

        <div className="p-6 space-y-6">
          {/* Confirmation Message */}
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Calendar" size={32} className="text-primary" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              Schicht bestätigen
            </h3>
            <p className="text-muted-foreground">
              Möchten Sie diese Schicht wirklich buchen?
            </p>
          </div>

          {/* Shift Details */}
          <div className="bg-muted rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Datum:</span>
              <span className="text-sm font-medium text-foreground">
                {formatDate(shift?.date)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Zeit:</span>
              <span className="text-sm font-medium text-foreground">
                {formatTime(shift?.startTime)} - {formatTime(shift?.endTime)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Dauer:</span>
              <span className="text-sm font-medium text-foreground">
                {shift?.duration} Stunden
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Abteilung:</span>
              <span className="text-sm font-medium text-foreground">
                {getDepartmentLabel(shift?.department)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Standort:</span>
              <span className="text-sm font-medium text-foreground">
                {shift?.location || 'Hauptgebäude, Etage 2'}
              </span>
            </div>
          </div>

          {/* Special Features */}
          {(shift?.isPriority || shift?.hasCrossTraining) && (
            <div className="space-y-2">
              {shift?.isPriority && (
                <div className="flex items-center space-x-2 text-amber-700">
                  <Icon name="Star" size={16} />
                  <span className="text-sm font-medium">Prioritäts-Schicht</span>
                </div>
              )}
              {shift?.hasCrossTraining && (
                <div className="flex items-center space-x-2 text-emerald-700">
                  <Icon name="GraduationCap" size={16} />
                  <span className="text-sm font-medium">Weiterbildungsmöglichkeit</span>
                </div>
              )}
            </div>
          )}

          {/* Important Notes */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Wichtige Hinweise:</p>
                <ul className="text-xs space-y-1">
                  <li>• Die Buchung ist verbindlich und wird sofort bestätigt</li>
                  <li>• Stornierungen sind bis 24h vor Schichtbeginn möglich</li>
                  <li>• Sie erhalten eine Bestätigung per E-Mail</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isLoading}
          >
            Abbrechen
          </Button>
          <Button
            variant="default"
            onClick={() => onConfirm(shift?.id)}
            loading={isLoading}
            iconName="Calendar"
            iconPosition="left"
          >
            Schicht buchen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmationModal;